/**
 * Client for the Admin Console API (`/api/v1/admin/users/`, RBAC Phase 3/4).
 *
 * The surface is company-scoped server-side: cloud-core only returns users who
 * share a company with the caller, and `companies` only lists the memberships
 * inside the caller's own companies. While `ADMIN_CONSOLE_ENABLED` is off the
 * whole surface answers 404, matching the flag that hides the console UI.
 *
 * Responses are camelCased by cloud-core's renderer and request bodies are
 * camelCase-parsed, so the shapes here mirror `AdminUserSerializer` verbatim.
 */
import API from "Utils/api";

/** The roles an admin may assign (`ASSIGNABLE_ROLES`): never `Cyngn-Vehicle`. */
export const ASSIGNABLE_ROLES = [
  "Cyngn-Admin",
  "Cyngn-Operator",
  "Cyngn-Viewer",
] as const;

export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export interface AdminCompanyAssignment {
  companyId: string;
  companyName: string | null;
  role: string | null;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  companyId: string | null;
  authProvider: string | null;
  authMode: string | null;
  companies: AdminCompanyAssignment[];
}

export interface InviteUserPayload {
  email: string;
  company: string;
  role?: AssignableRole;
  firstName?: string;
  lastName?: string;
}

export const getAdminUsers = async (): Promise<AdminUser[]> => {
  const response = await API.get("admin/users/");
  return response.data.users;
};

export const inviteAdminUser = async (
  payload: InviteUserPayload
): Promise<AdminUser> => {
  const response = await API.post("admin/users/", payload);
  return response.data;
};

export const setAdminUserRole = async (
  userId: string,
  companyId: string,
  role: AssignableRole
): Promise<AdminUser> => {
  const response = await API.put(`admin/users/${userId}/role/`, {
    company: companyId,
    role,
  });
  return response.data;
};

export const setAdminUserActive = async (
  userId: string,
  isActive: boolean
): Promise<AdminUser> => {
  const response = await API.put(`admin/users/${userId}/active/`, {
    isActive,
  });
  return response.data;
};

export const assignAdminUserCompany = async (
  userId: string,
  companyId: string,
  role?: AssignableRole
): Promise<AdminUser> => {
  const response = await API.post(`admin/users/${userId}/companies/`, {
    company: companyId,
    ...(role ? { role } : {}),
  });
  return response.data;
};

/**
 * Revoking the caller's last shared company with the user answers 204: the
 * user is no longer theirs to see, so `null` tells the console to drop them.
 */
export const revokeAdminUserCompany = async (
  userId: string,
  companyId: string
): Promise<AdminUser | null> => {
  const response = await API.delete(
    `admin/users/${userId}/companies/${companyId}/`
  );
  return response.status === 204 ? null : response.data;
};
